import { useCallback, useEffect } from 'react'
import { flushSync } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { onboardingApi } from '@/api/endpoints'
import { useAuthStore } from '@/store/auth'
import { toast } from '@/store/toast'
import type { OnboardingState, OnboardingStepKey } from '@/types/api'

export const ONBOARDING_KEY = ['onboarding'] as const

export function useOnboardingState() {
  return useQuery<OnboardingState>({
    queryKey: ONBOARDING_KEY,
    queryFn: onboardingApi.getState,
    staleTime: 10_000,
  })
}

/**
 * Step submission.
 *
 * Server validation errors are surfaced to the caller as a field map rather
 * than a toast, so each input can show its own message inline.
 */
export function useSaveStep(step: OnboardingStepKey) {
  const queryClient = useQueryClient()
  const setOnboarding = useAuthStore((s) => s.setOnboarding)

  return useMutation<OnboardingState, ApiError, unknown>({
    mutationFn: (payload) => onboardingApi.saveStep(step, payload),
    onSuccess: (state) => {
      queryClient.setQueryData(ONBOARDING_KEY, state)
      setOnboarding({
        currentStep: state.currentStep,
        currentIndex: state.currentIndex,
        completedSteps: state.completedSteps,
        percentComplete: state.percentComplete,
        isComplete: state.isComplete,
      })
    },
    onError: (error) => {
      // Field-level problems are rendered next to the inputs; anything else is
      // a genuine surprise and deserves a toast.
      if (Object.keys(error.fieldErrors).length === 0) {
        toast.error('Could not save', error.message)
      }
    },
  })
}

export function useSkipStep() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ step, reason }: { step: OnboardingStepKey; reason?: string }) =>
      onboardingApi.skip(step, reason),
    onSuccess: (state) => queryClient.setQueryData(ONBOARDING_KEY, state),
    onError: (error: ApiError) => toast.error('Could not skip', error.message),
  })
}

export function useNavigateStep() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (step: OnboardingStepKey) => onboardingApi.navigate(step),
    onSuccess: (state) => queryClient.setQueryData(ONBOARDING_KEY, state),
  })
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const setOnboarding = useAuthStore((s) => s.setOnboarding)

  return useMutation({
    mutationFn: onboardingApi.complete,
    onSuccess: (result) => {
      // One render for both: the wizard's own guard would otherwise send them to /dashboard first.
      flushSync(() => {
        navigate(result.redirectTo, { replace: true })
        setOnboarding({ isComplete: true })
      })
      queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY })
      toast.success("You're all set", 'We are setting up your job portals now.')
    },
    onError: (error: ApiError) => {
      const missing = (error.problem?.missingSteps as string[] | undefined) ?? []
      toast.error(
        'Setup is not finished',
        missing.length ? `Still needed: ${missing.join(', ')}` : error.message,
      )
    },
  })
}

/**
 * Enter submits the current step.
 *
 * Skipped while focus is in a textarea (where Enter means newline) or on a
 * button (where Enter already activates it).
 */
export function useEnterToContinue(onSubmit: () => void, enabled = true) {
  const handler = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled || event.key !== 'Enter' || event.shiftKey) return
      const target = event.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'TEXTAREA' || tag === 'BUTTON' || target?.isContentEditable) return
      event.preventDefault()
      onSubmit()
    },
    [onSubmit, enabled],
  )

  useEffect(() => {
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handler])
}
